// ===== Production Call Interface Component =====

import React, { memo, useState, useEffect } from 'react';
import { 
  Box, 
  IconButton, 
  Typography,
  Chip
} from '@mui/material';
import {
  CallEnd,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
  VolumeOff,
  ScreenShare,
  StopScreenShare,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import { CALL_STATES } from '../constants/ChatConstants';

const CallInterface = ({
  callType,
  callStatus,
  callError,
  isMuted,
  isVideoOff,
  isSpeakerOn,
  isScreenSharing,
  localVideoRef,
  remoteVideoRef,
  onToggleMute,
  onToggleVideo,
  onToggleSpeaker,
  onToggleScreenShare,
  onEndCall
}) => {
  const [callDuration, setCallDuration] = useState(0);

  // Timer for call duration
  useEffect(() => {
    let interval = null;
    if (callStatus === CALL_STATES.CONNECTED) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStatus]);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusMessage = () => {
    switch (callStatus) {
      case CALL_STATES.CALLING:
        return '📞 Starting call...';
      case CALL_STATES.RINGING:
        return '📞 Ringing...';
      case CALL_STATES.CONNECTING:
        return '📞 Connecting...';
      case CALL_STATES.CONNECTED:
        return null; // Don't show status when connected
      case CALL_STATES.RECONNECTING:
        return '🔄 Reconnecting...';
      case CALL_STATES.REJECTED:
        return (
          <Box textAlign="center">
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>📞❌</div>
            <div>Call Declined</div>
            <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '4px' }}>
              The user declined your call
            </div>
          </Box>
        );
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <Box
      style={{
        position: 'relative',
        width: '90%',
        height: '90%',
        background: callStatus === CALL_STATES.CONNECTED 
          ? '#000' 
          : 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
        borderRadius: '12px',
        color: 'white'
      }}
    >
      {/* Remote Video - Always render and visible */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          backgroundColor: '#000'
        }}
      />

      {/* Local Video (Picture-in-Picture) - Always render for video calls */}
      {callType === 'video' && (
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '120px',
            height: '90px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '2px solid white',
            backgroundColor: '#333',
            zIndex: 2
          }}
        />
      )}

      {/* WhatsApp-style Call Screen - Before Connected */}
      {callStatus !== CALL_STATES.CONNECTED && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 20px',
            zIndex: 3
          }}
        >
          {/* User Avatar */}
          <Box
            style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              backgroundColor: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              fontWeight: 'bold',
              marginBottom: '30px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              border: '4px solid rgba(255,255,255,0.2)'
            }}
          >
            U
          </Box>

          {/* User Name */}
          <Typography
            variant="h4"
            style={{
              fontSize: '28px',
              fontWeight: '300',
              marginBottom: '12px',
              textAlign: 'center',
              color: 'white'
            }}
          >
            User Name
          </Typography>

          {/* Call Status */}
          <Typography
            variant="body1"
            style={{
              fontSize: '16px',
              opacity: 0.9,
              textAlign: 'center',
              color: 'white',
              marginBottom: '8px'
            }}
          >
            {statusMessage || (callType === 'video' ? 'Video calling...' : 'Calling...')}
          </Typography>

          {/* Call Info */}
          <Typography
            variant="body2"
            style={{
              fontSize: '14px',
              opacity: 0.7,
              textAlign: 'center',
              color: 'white'
            }}
          >
            {callType === 'video' ? '📹 Video Call' : '📞 Voice Call'}
          </Typography>
        </Box>
      )}

      {/* Call Error Overlay */}
      {callError && (
        <Box
          style={{
            position: 'absolute',
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255,0,0,0.9)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            maxWidth: '80%',
            textAlign: 'center',
            zIndex: 4
          }}
        >
          {callError}
        </Box>
      )}

      {/* Connection Quality Indicator */}
      <Chip
        label={callStatus === CALL_STATES.CONNECTED ? '🟢 Connected' : '🟡 Connecting'}
        size="small"
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          backgroundColor: 'rgba(0,0,0,0.6)',
          color: 'white',
          fontSize: '12px',
          zIndex: 5
        }}
      />


      {/* Call Controls */}
      <Box
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '16px',
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: '12px 20px',
          borderRadius: '30px',
          backdropFilter: 'blur(10px)',
          zIndex: 10
        }}
      >
        {/* Mute Button */}
        <IconButton
          onClick={onToggleMute}
          style={{
            backgroundColor: isMuted ? '#ff4444' : 'rgba(255,255,255,0.2)',
            color: 'white',
            width: '50px',
            height: '50px',
            transition: 'all 0.2s ease'
          }}
          title={isMuted ? 'Unmute Audio' : 'Mute Audio'}
        >
          {isMuted ? <MicOff /> : <Mic />}
        </IconButton>

        {/* Video Toggle (for video calls) */}
        {callType === 'video' && (
          <IconButton
            onClick={onToggleVideo}
            style={{
              backgroundColor: isVideoOff ? '#ff4444' : 'rgba(255,255,255,0.2)',
              color: 'white',
              width: '50px',
              height: '50px',
              transition: 'all 0.2s ease'
            }}
            title={isVideoOff ? 'Turn On Video' : 'Turn Off Video'}
          >
            {isVideoOff ? <VideocamOff /> : <Videocam />}
          </IconButton>
        )}

        {/* Speaker Button */}
        <IconButton
          onClick={onToggleSpeaker}
          style={{
            backgroundColor: isSpeakerOn ? '#25D366' : 'rgba(255,255,255,0.2)',
            color: 'white',
            width: '50px',
            height: '50px',
            transition: 'all 0.2s ease'
          }}
          title={isSpeakerOn ? 'Turn Off Speaker' : 'Turn On Speaker'}
        >
          {isSpeakerOn ? <VolumeUp /> : <VolumeOff />}
        </IconButton>

        {/* Screen Share Button (for video calls) */}
        {callType === 'video' && (
          <IconButton
            onClick={onToggleScreenShare}
            style={{
              backgroundColor: isScreenSharing ? '#25D366' : 'rgba(255,255,255,0.2)',
              color: 'white',
              width: '50px',
              height: '50px',
              transition: 'all 0.2s ease'
            }}
            title={isScreenSharing ? 'Stop Screen Share' : 'Start Screen Share'}
          >
            {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
          </IconButton>
        )}

        {/* End Call Button */}
        <IconButton
          onClick={onEndCall}
          style={{
            backgroundColor: '#ff4444',
            color: 'white',
            width: '50px',
            height: '50px',
            transition: 'all 0.2s ease'
          }}
          title="End Call"
        >
          <CallEnd />
        </IconButton>
      </Box>

      {/* Call Duration Timer - Left Bottom */}
      {callStatus === CALL_STATES.CONNECTED && (
        <Box
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 10
          }}
        >
          🕒 {formatDuration(callDuration)}
        </Box>
      )}

      {/* Media State Indicators */}
      <Box
        style={{
          position: 'absolute',
          bottom: '140px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 10
        }}
      >
        {isMuted && (
          <Chip
            icon={<MicOff />}
            label="Muted"
            size="small"
            style={{
              backgroundColor: 'rgba(255,68,68,0.8)',
              color: 'white'
            }}
          />
        )}
        {isVideoOff && callType === 'video' && (
          <Chip
            icon={<VideocamOff />}
            label="Video Off"
            size="small"
            style={{
              backgroundColor: 'rgba(255,68,68,0.8)',
              color: 'white'
            }}
          />
        )}
        {isScreenSharing && (
          <Chip
            icon={<ScreenShare />}
            label="Sharing Screen"
            size="small"
            style={{
              backgroundColor: 'rgba(37,211,102,0.8)',
              color: 'white'
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default memo(CallInterface);
